interface AudioConfig {
  autoPlay: boolean;
  loop: boolean;
  muted: boolean;
  isBase64: boolean;
}

export default class AudioPlayer {
  private audioPlayer: HTMLAudioElement;
  private policyEscaped: boolean;
  private playbackGeneration = 0;
  private pendingPlayback: {
    generation: number;
    resolve: (result: boolean) => void;
  } | null = null;
  config: AudioConfig | undefined;

  public onloadeddata: ((e: Event) => void) | undefined;
  public onplay: ((e: Event) => void) | undefined;
  public onpause: ((e: Event) => void) | undefined;
  public onemptied: ((e: Event) => void) | undefined;
  public onended: ((e: Event) => void) | undefined;
  public onerror: ((e: Event | string) => void) | undefined;

  constructor(config: Partial<AudioConfig> = {}) {
    this.audioPlayer = new Audio();
    this.policyEscaped = false;

    if (config) this.config = {
      autoPlay: false,
      loop: false,
      muted: false,
      isBase64: false,
      ...config
    };

    this.audioPlayer.autoplay = this.config?.autoPlay ?? false;
    this.audioPlayer.loop = this.config?.loop ?? false;
    this.audioPlayer.muted = this.config?.muted ?? false;

    this.init();
  }

  private init() {
    this.audioPlayer.onloadeddata = ((e: Event) => {
      // console.log("😀onloadeddata", e);
      if (this.onloadeddata) this.onloadeddata.bind(this)(e)
    }).bind(this);

    this.audioPlayer.onplay = ((e: Event) => {
      // console.log("😀onplay", e);
      if (this.onplay) this.onplay.bind(this)(e)
    }).bind(this);

    this.audioPlayer.onpause = ((e: Event) => {
      // console.log("😀onpause", e);
      if (this.onpause) this.onpause.bind(this)(e)
    }).bind(this);

    this.audioPlayer.onemptied = ((e: Event) => {
      // console.log("😀onemptied", e);
      if (this.onemptied) this.onemptied.bind(this)(e)
    }).bind(this);

    this.audioPlayer.onended = ((e: Event) => {
      // 교체된 소스가 재생 중이면 이전 소스의 늦은 ended 이벤트이므로 무시한다.
      if (!this.audioPlayer.ended) return;
      const finishedCurrentPlayback = this.finishPendingPlayback(true);
      if (finishedCurrentPlayback && this.onended) this.onended.bind(this)(e)
    }).bind(this);

    this.audioPlayer.onerror = ((e: string | Event) => {
      // 새 소스로 교체된 뒤 남은 이전 error 이벤트는 current error가 없으므로 무시한다.
      if (!this.audioPlayer.error) return;
      const failedCurrentPlayback = this.finishPendingPlayback(false);
      if (failedCurrentPlayback && this.onerror) this.onerror.bind(this)(e)
    }).bind(this);
  }

  private invalidatePendingPlayback(): void {
    this.playbackGeneration += 1;
    if (this.pendingPlayback) {
      this.pendingPlayback.resolve(false);
      this.pendingPlayback = null;
    }
  }

  private finishPendingPlayback(result: boolean): boolean {
    if (!this.pendingPlayback) return false;
    const { resolve } = this.pendingPlayback;
    this.pendingPlayback = null;
    resolve(result);
    return true;
  }

  public escapePolicy() {
    if (typeof window === 'undefined') return;

    if (this.policyEscaped) return;
    try {
      this.audioPlayer.play();
      this.audioPlayer.pause();
    } catch {
      // pass
    } finally {
      this.policyEscaped = true;
    }
  }

  public setSrc(src: string | MediaProvider) {
    if (!this.audioPlayer || !this.config) return;
    this.invalidatePendingPlayback();

    if (typeof src === "string") {
      if (this.audioPlayer.src) URL.revokeObjectURL((this.config.isBase64 ? "data:audio/wav;base64," : "") + this.audioPlayer.src);
      this.audioPlayer.src = (this.config.isBase64 ? "data:audio/wav;base64," : "") + src;
    } else {
      this.audioPlayer.srcObject = src;
    }

    this.audioPlayer.load();
  }

  public play(src: string = "", onStarted?: () => void): Promise<boolean> {
    if (src) {
      this.setSrc(src);
    } else {
      this.invalidatePendingPlayback();
    }

    const generation = this.playbackGeneration;
    return new Promise((resolve) => {
      this.pendingPlayback = { generation, resolve };

      try {
        const playPromise = this.audioPlayer.play();
        if (playPromise && typeof playPromise.then === 'function') {
          playPromise.then(() => {
            if (this.pendingPlayback?.generation === generation) {
              onStarted?.();
            }
          }).catch((error: DOMException) => {
            console.error('🔊 [AudioPlayer] play() 실패:', error.name, error.message);
            if (this.pendingPlayback?.generation === generation) {
              this.finishPendingPlayback(false);
            }
          });
        }
      } catch (error) {
        console.error('🔊 [AudioPlayer] play() 동기 에러:', error);
        if (this.pendingPlayback?.generation === generation) {
          this.finishPendingPlayback(false);
        }
      }
    });
  }

  public pause(fadeOutDuration?: number) {
    this.invalidatePendingPlayback();
    if (fadeOutDuration && fadeOutDuration > 0) {
      const initialVolume = this.audioPlayer.volume;
      const stepTime = 50; // ms
      const steps = Math.floor(fadeOutDuration / stepTime);
      const volumeDecrement = initialVolume / steps;
      let currentStep = 0;
      const interval = setInterval(() => {
        currentStep++;
        const newVolume = Math.max(initialVolume - volumeDecrement * currentStep, 0);
        this.audioPlayer.volume = newVolume;
        if (currentStep >= steps) {
          clearInterval(interval);
          this.audioPlayer.pause();
          // 옵션: pause 후 원래 볼륨 복원
          this.audioPlayer.volume = initialVolume;
        }
      }, stepTime);
    } else {
      this.audioPlayer.pause();
    }
  }

  public reset() {
    this.pause()
    if (this.audioPlayer.src) URL.revokeObjectURL((this.config?.isBase64 ? "data:audio/wav;base64," : "") + this.audioPlayer.src);
    this.audioPlayer.src = "";
    this.audioPlayer.srcObject = null;
  }

  public getAudioPlayer(): HTMLAudioElement {
    return this.audioPlayer;
  }

  setVolume(volume: number) {
    // iOS Safari에서 volume = 0이 작동하지 않는 문제 해결
    // volume이 0일 때는 muted 속성을 사용하여 확실한 음소거 구현
    if (volume === 0) {
      this.audioPlayer.muted = true;
      this.audioPlayer.volume = 0;
    } else {
      this.audioPlayer.muted = false;
      this.audioPlayer.volume = volume; // 0-1 범위로 제한
    }
  }

  public destroy() {
    // 1. 먼저 재생 중지 및 소스 초기화
    this.reset();

    // 2. 모든 이벤트 리스너 제거
    this.audioPlayer.onloadeddata = null;
    this.audioPlayer.onplay = null;
    this.audioPlayer.onpause = null;
    this.audioPlayer.onemptied = null;
    this.audioPlayer.onended = null;
    this.audioPlayer.onerror = null;

    // 3. 클래스의 이벤트 핸들러도 정리
    this.onloadeddata = undefined;
    this.onplay = undefined;
    this.onpause = undefined;
    this.onemptied = undefined;
    this.onended = undefined;
    this.onerror = undefined;

    // 4. 오디오 엘리먼트 정리
    // this.audioPlayer = null;
  }
}
